import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import type {
  Assignment, CallOutcome, Customer, LogEntry, LogType, NewBizProspect,
  NewBizSource, PipelineStatus, Quote, QuoteStatus, User
} from './types';
import { useAppStore } from './store';
import { Login } from './components/Login';
import { Sidebar, type ViewKey } from './components/Sidebar';
import { Dashboard, type ActivityFilter } from './components/Dashboard';
import { TodayTargets } from './components/TodayTargets';
import { MyPipeline } from './components/MyPipeline';
import { EnhancedMgmt } from './components/EnhancedMgmt';
import { Quotes } from './components/Quotes';
import { Toast } from './components/Toast';
import { ConfirmModal, CustomerModal, LogModal, LogEditModal, UserModal } from './components/Modals';
import {
  makePipelineTarget, NewBizModal, PipelineModal, ReassignModal,
  type PipelineTarget
} from './components/PipelineModals';
import { QuoteModal, QuoteUpdateModal } from './components/QuoteModals';

const ROLE_LABEL: Record<User['role'], string> = { admin: 'Admin', am: 'Account Manager', jam: 'Junior AM' };
type FilterType = ActivityFilter;

interface DeletePending {
  kind: 'log' | 'customer' | 'user' | 'assign' | 'newbiz' | 'quote';
  id: string;
  title: string;
  message: string;
}

interface ReassignPending {
  kind: 'assign' | 'newbiz';
  id: string;
  currentAm: string;
}

export function App() {
  const { state, actions, loading } = useAppStore();
  const [view, setView] = useState<ViewKey>('all');
  const [filterCustomer, setFilterCustomer] = useState<string | null>(null);
  const [filterAM, setFilterAM] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQ, setSearchQ] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [now, setNow] = useState(() => new Date());

  const [logModalType, setLogModalType] = useState<LogType | null>(null);
  const [editingLog, setEditingLog] = useState<LogEntry | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [pipelineTarget, setPipelineTarget] = useState<PipelineTarget | null>(null);
  const [newBizDefaultAm, setNewBizDefaultAm] = useState<string | null>(null);
  const [reassign, setReassign] = useState<ReassignPending | null>(null);
  const [confirm, setConfirm] = useState<DeletePending | null>(null);
  const [busyMessage, setBusyMessage] = useState<string | null>(null);

  const [toastMsg, setToastMsg] = useState('');
  const [toastShow, setToastShow] = useState(false);
  const toastTimer = useRef<number | null>(null);

  function showToast(msg: string) {
    setToastMsg(msg);
    setToastShow(true);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToastShow(false), 2500);
  }

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const visibleCustomers = useMemo(() => {
    if (!state.user) return [];
    let cs = state.user.role === 'admin' ? state.customers : state.customers.filter(c => c.amName === state.user!.name);
    if (state.user.role === 'admin' && filterAM) cs = cs.filter(c => c.amName === filterAM);
    return cs;
  }, [state.user, state.customers, filterAM]);

  const closeSidebar = () => setSidebarOpen(false);

  function selectCustomer(id: string | null) {
    setFilterCustomer(id);
    if (id) setView('all');
    closeSidebar();
  }

  function setAMFilter(am: string | null) {
    setFilterAM(am);
    setFilterCustomer(null);
    setView('all');
    closeSidebar();
  }

  function changeView(v: ViewKey) {
    setView(v);
    setFilterCustomer(null);
    closeSidebar();
  }

  async function logout() {
    try { await api.logout(); } catch { /* ignore */ }
    actions.setUser(null);
    setView('all'); setFilterCustomer(null); setFilterAM(null); setFilterType('all'); setSearchQ('');
  }

  // ── Saves ───────────────────────────────────────────────────────────────────
  async function saveLog(customerId: string, notes: string) {
    if (!logModalType) return;
    if (!notes.trim()) { showToast('Please enter some notes.'); return; }
    if (!customerId) { showToast('Please select a customer.'); return; }
    try {
      const created = await api.addLog({ type: logModalType, customerId, notes });
      actions.addLogLocal(created);
      setLogModalType(null);
      showToast('Entry saved ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to save.');
    }
  }

  async function saveLogEdit(patch: { type: LogType; notes: string }) {
    if (!editingLog) return;
    if (!patch.notes.trim()) { showToast('Please enter some notes.'); return; }
    try {
      const updated = await api.updateLog(editingLog.id, patch);
      actions.updateLogLocal(updated);
      setEditingLog(null);
      showToast('Entry updated ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update.');
    }
  }

  async function saveQuote(data: { customerId: string; clarityRef: string; notes: string; revenue: number }) {
    if (!data.customerId) { showToast('Please select a customer.'); return; }
    if (!data.clarityRef) { showToast('Clarity reference is required.'); return; }
    try {
      const created = await api.addQuote(data);
      actions.upsertQuoteLocal(created);
      setShowQuoteModal(false);
      showToast('Quote saved ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to save quote.');
    }
  }

  async function setQuoteStatus(q: Quote, status: QuoteStatus) {
    try {
      const updated = await api.updateQuote(q.id, { status });
      actions.upsertQuoteLocal(updated);
      showToast(status === 'won' ? 'Marked won ✓' : status === 'lost' ? 'Marked lost ✗' : 'Reopened.');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed.');
    }
  }

  async function saveQuoteUpdate(patch: { status: QuoteStatus; revenue: number; notes: string; clarityRef: string }) {
    if (!editingQuote) return;
    if (!patch.clarityRef) { showToast('Clarity reference is required.'); return; }
    try {
      const updated = await api.updateQuote(editingQuote.id, patch);
      actions.upsertQuoteLocal(updated);
      setEditingQuote(null);
      showToast('Quote updated ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update quote.');
    }
  }

  async function saveCustomer(data: { name: string; amName: string; contact: string; phone: string; email: string }) {
    if (!data.name.trim()) { showToast('Customer name is required.'); return; }
    try {
      const created = await api.addCustomer(data);
      actions.addCustomerLocal(created);
      setShowCustomerModal(false);
      showToast('Customer added ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to add customer.');
    }
  }

  async function saveUser(data: { name: string; username: string; email: string; role: 'am' | 'jam'; password: string }) {
    if (!data.name || !data.username || !data.email || !data.password) { showToast('All fields required.'); return; }
    if (data.password.length < 6) { showToast('Password must be at least 6 characters.'); return; }
    try {
      const created = await api.addUser(data);
      actions.addUserLocal(created);
      setShowUserModal(false);
      showToast('User added ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to add user.');
    }
  }

  async function savePipeline(patch: { status: PipelineStatus; outcome: CallOutcome | null; revenue: number; notes: string }) {
    if (!pipelineTarget) return;
    try {
      if (pipelineTarget.kind === 'newbiz') {
        const updated = await api.updateNewBiz(pipelineTarget.id, patch);
        actions.upsertNewBizLocal(updated);
      } else {
        const updated = await api.updateAssignment(pipelineTarget.id, patch);
        actions.upsertAssignmentLocal(updated);
      }
      setPipelineTarget(null);
      showToast('Account updated ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to update.');
    }
  }

  async function saveNewBiz(data: {
    amName: string; companyName: string; contact: string; phone: string;
    email: string; source: NewBizSource; notes: string;
  }) {
    if (!data.companyName) { showToast('Company name is required.'); return; }
    try {
      const created = await api.addNewBiz(data);
      actions.upsertNewBizLocal(created);
      setNewBizDefaultAm(null);
      showToast('New prospect added ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to add prospect.');
    }
  }

  async function saveReassign(toAm: string) {
    if (!reassign) return;
    try {
      if (reassign.kind === 'newbiz') {
        const updated = await api.updateNewBiz(reassign.id, { amName: toAm });
        actions.upsertNewBizLocal(updated);
      } else {
        const updated = await api.updateAssignment(reassign.id, { amName: toAm });
        actions.upsertAssignmentLocal(updated);
      }
      setReassign(null);
      showToast('Account reassigned ✓');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed to reassign.');
    }
  }

  // ── Pipeline modal helpers ──────────────────────────────────────────────────
  function openPipelineFor(kind: 'assign' | 'newbiz', id: string) {
    const item = kind === 'newbiz'
      ? state.newbiz.find(n => n.id === id)
      : state.assignments.find(a => a.id === id);
    if (!item) { showToast('Item not found.'); return; }
    setPipelineTarget(makePipelineTarget(item, kind));
  }

  // ── Bulk admin actions ──────────────────────────────────────────────────────
  async function autoAssignAm(role: 'junior' | 'senior', amName: string) {
    setBusyMessage(`Assigning ${amName}…`);
    try {
      const r = await api.bulkAutoAssign({ mode: 'am', amName });
      await actions.refreshPipeline();
      showToast(r.message);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed.');
    } finally {
      setBusyMessage(null);
      void role; // silences unused warning; kept for parity with HTML signature
    }
  }
  async function autoAssignAll() {
    setBusyMessage('Auto-assigning all AMs…');
    try {
      const r = await api.bulkAutoAssign({ mode: 'all' });
      await actions.refreshPipeline();
      showToast(r.message);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed.');
    } finally {
      setBusyMessage(null);
    }
  }
  async function clearCycle(amName: string) {
    if (!window.confirm(`Clear all current-cycle assignments for ${amName}?`)) return;
    try {
      const r = await api.bulkClearCycle(amName);
      await actions.refreshPipeline();
      showToast(r.message);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed.');
    }
  }
  async function markOverdueLost() {
    if (!window.confirm('Mark all overdue accounts (across every AM) as Lost?')) return;
    try {
      const r = await api.bulkMarkOverdueLost();
      await actions.refreshPipeline();
      showToast(r.message);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Failed.');
    }
  }

  // ── Confirm/delete dispatch ─────────────────────────────────────────────────
  async function runConfirm() {
    if (!confirm) return;
    const c = confirm;
    setConfirm(null);
    try {
      if (c.kind === 'log') {
        await api.deleteLog(c.id); actions.removeLogLocal(c.id); showToast('Entry deleted.');
      } else if (c.kind === 'customer') {
        await api.deleteCustomer(c.id); actions.removeCustomerLocal(c.id); showToast('Customer removed.');
      } else if (c.kind === 'user') {
        await api.deleteUser(c.id); actions.removeUserLocal(c.id); showToast('User removed.');
      } else if (c.kind === 'assign') {
        await api.deleteAssignment(c.id); actions.removeAssignmentLocal(c.id); showToast('Assignment removed.');
      } else if (c.kind === 'newbiz') {
        await api.deleteNewBiz(c.id); actions.removeNewBizLocal(c.id); showToast('Prospect removed.');
      } else if (c.kind === 'quote') {
        await api.deleteQuote(c.id); actions.removeQuoteLocal(c.id); showToast('Quote removed.');
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Action failed.');
    }
  }

  if (loading) {
    return <div className="login-screen"><div className="login-box"><div className="login-sub">Loading…</div></div></div>;
  }

  if (!state.user) {
    return <Login onLogin={async (u) => { actions.setUser(u); await actions.refreshAll(); }} />;
  }

  const dateLabel = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) +
    ' — ' + now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const logModalCustomers: Customer[] = visibleCustomers.length > 0 ? visibleCustomers : (state.user.role === 'admin' ? state.customers : []);

  // Users available as AM targets in modals (excludes the system 'admin' record).
  const amUserList: User[] = state.user.role === 'admin'
    ? state.users.filter(u => u.role === 'am' || u.role === 'jam')
    : [{ id: state.user.id, name: state.user.name, username: state.user.username, email: state.user.email, role: state.user.role }];

  return (
    <div className="app">
      <div className="header">
        <div className="header-left">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>☰</button>
          <div className="logo">Printvision Sales</div>
          <div className="date-display">{dateLabel}</div>
        </div>
        <div className="header-right">
          <div className="user-badge">
            <span>{state.user.name}</span>
            <span className="user-role">{ROLE_LABEL[state.user.role]}</span>
          </div>
          <button className="logout-btn" onClick={logout}>Sign Out</button>
        </div>
      </div>

      <div className="main">
        <Sidebar
          user={state.user}
          customers={state.customers}
          logs={state.logs}
          visibleCustomers={visibleCustomers}
          view={view}
          filterCustomer={filterCustomer}
          filterAM={filterAM}
          open={sidebarOpen}
          onSetView={changeView}
          onSelectCustomer={selectCustomer}
          onSetAMFilter={setAMFilter}
          onCloseSidebar={closeSidebar}
          onAddCustomer={() => setShowCustomerModal(true)}
        />

        <div className="content">
          {view === 'mgmt' && state.user.role === 'admin' ? (
            <EnhancedMgmt
              customers={state.customers}
              logs={state.logs}
              assignments={state.assignments}
              newbiz={state.newbiz}
              quotes={state.quotes}
              busyMessage={busyMessage}
              onUpdate={openPipelineFor}
              onReassign={(kind, id, currentAm) => setReassign({ kind, id, currentAm })}
              onDelete={(kind, id) => {
                const item = kind === 'newbiz'
                  ? state.newbiz.find(n => n.id === id)
                  : state.assignments.find(a => a.id === id);
                const name = kind === 'newbiz'
                  ? (item as NewBizProspect | undefined)?.companyName ?? 'this prospect'
                  : (item as Assignment | undefined)?.custName ?? 'this assignment';
                setConfirm({
                  kind, id,
                  title: kind === 'newbiz' ? 'Remove prospect?' : 'Remove assignment?',
                  message: `Remove ${name}? This action cannot be undone.`
                });
              }}
              onOpenNewBiz={(am) => setNewBizDefaultAm(am || state.user!.name)}
              onAutoAssignAm={autoAssignAm}
              onAutoAssignAll={autoAssignAll}
              onClearCycle={clearCycle}
              onMarkOverdueLost={markOverdueLost}
              onEditQuote={(q) => setEditingQuote(q)}
              onSetQuoteStatus={setQuoteStatus}
              onDeleteQuote={(q) => setConfirm({
                kind: 'quote', id: q.id,
                title: 'Remove quote?',
                message: `Remove the quote ${q.clarityRef} for ${q.customerName}?`
              })}
              onDeleteCustomer={(id) => {
                const cust = state.customers.find(c => c.id === id);
                setConfirm({
                  kind: 'customer', id,
                  title: 'Remove customer?',
                  message: `Remove ${cust?.name ?? 'this customer'}? All linked logs, quotes, and assignments will also be deleted.`
                });
              }}
            />
          ) : view === 'targets' ? (
            <TodayTargets
              user={state.user}
              assignments={state.assignments}
              newbiz={state.newbiz}
              logs={state.logs}
              onOpenNewBiz={() => setNewBizDefaultAm(state.user!.name)}
              onSwitchToPipeline={() => setView('pipeline')}
              onUpdate={openPipelineFor}
            />
          ) : view === 'quotes' ? (
            <Quotes
              user={state.user}
              quotes={state.quotes}
              onAddQuote={() => setShowQuoteModal(true)}
              onEdit={(q) => setEditingQuote(q)}
              onSetStatus={setQuoteStatus}
              onDelete={(q) => setConfirm({
                kind: 'quote', id: q.id,
                title: 'Remove quote?',
                message: `Remove the quote ${q.clarityRef}?`
              })}
            />
          ) : view === 'pipeline' ? (
            <MyPipeline
              user={state.user}
              customers={state.customers}
              assignments={state.assignments}
              newbiz={state.newbiz}
              logs={state.logs}
              onOpenNewBiz={() => setNewBizDefaultAm(state.user!.name)}
              onAutoAssignAll={autoAssignAll}
              onUpdate={openPipelineFor}
              onReassign={(kind, id, currentAm) => setReassign({ kind, id, currentAm })}
              onDelete={(kind, id) => setConfirm({
                kind, id,
                title: kind === 'newbiz' ? 'Remove prospect?' : 'Remove assignment?',
                message: 'This action cannot be undone.'
              })}
            />
          ) : (
            <Dashboard
              user={state.user}
              customers={state.customers}
              visibleCustomers={visibleCustomers}
              logs={state.logs}
              quotes={state.quotes}
              filterCustomer={filterCustomer}
              filterAM={filterAM}
              filterType={filterType}
              searchQ={searchQ}
              view={view === 'today' ? 'today' : 'all'}
              onOpenLog={(t) => setLogModalType(t)}
              onOpenQuote={() => setShowQuoteModal(true)}
              onSetFilterType={setFilterType}
              onSetSearch={setSearchQ}
              onEditLog={(l) => setEditingLog(l)}
              onDeleteLog={(id) => setConfirm({
                kind: 'log', id,
                title: 'Delete entry?',
                message: 'This action cannot be undone. The log entry will be permanently removed.'
              })}
              onEditQuote={(q) => setEditingQuote(q)}
              onSetQuoteStatus={setQuoteStatus}
              onDeleteQuote={(q) => setConfirm({
                kind: 'quote', id: q.id,
                title: 'Remove quote?',
                message: `Remove the quote ${q.clarityRef}? This action cannot be undone.`
              })}
            />
          )}
        </div>
      </div>

      {logModalType && (
        <LogModal
          type={logModalType}
          customers={logModalCustomers}
          defaultCustomerId={filterCustomer}
          onCancel={() => setLogModalType(null)}
          onSave={saveLog}
        />
      )}
      {editingLog && (
        <LogEditModal
          log={editingLog}
          onCancel={() => setEditingLog(null)}
          onSave={saveLogEdit}
        />
      )}
      {showQuoteModal && (
        <QuoteModal
          customers={logModalCustomers}
          defaultCustomerId={filterCustomer}
          onCancel={() => setShowQuoteModal(false)}
          onSave={saveQuote}
        />
      )}
      {editingQuote && (
        <QuoteUpdateModal
          quote={editingQuote}
          onCancel={() => setEditingQuote(null)}
          onSave={saveQuoteUpdate}
        />
      )}
      {showCustomerModal && (
        <CustomerModal
          role={state.user.role}
          users={state.users}
          currentUserName={state.user.name}
          onCancel={() => setShowCustomerModal(false)}
          onSave={saveCustomer}
        />
      )}
      {showUserModal && (
        <UserModal onCancel={() => setShowUserModal(false)} onSave={saveUser} />
      )}
      {pipelineTarget && (
        <PipelineModal
          target={pipelineTarget}
          onCancel={() => setPipelineTarget(null)}
          onSave={savePipeline}
        />
      )}
      {newBizDefaultAm !== null && (
        <NewBizModal
          defaultAm={newBizDefaultAm || state.user.name}
          isAdmin={state.user.role === 'admin'}
          users={amUserList}
          onCancel={() => setNewBizDefaultAm(null)}
          onSave={saveNewBiz}
        />
      )}
      {reassign && (
        <ReassignModal
          currentAm={reassign.currentAm}
          users={amUserList}
          onCancel={() => setReassign(null)}
          onSave={saveReassign}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={runConfirm}
        />
      )}

      <Toast message={toastMsg} show={toastShow} />
    </div>
  );
}
